import { memo, useCallback, useMemo } from "react";

import { type Maybe } from "@shopify/hydrogen-react/storefront-api-types";

import { ColorSwatch } from "@react/components/ColorSwatch";
import { VariantSelector } from "@react/components/VariantSelector/VariantSelector.data";
import { handleize, findSelectedOption } from "@react/utilities";
import { type TTailwindString } from "@root/@types/classnames";
import { SelectedVariantAvailability } from "@root/tablescape/constants";
import {
  type CartProductVariant,
  type CartRenderVariantOptions,
} from "@tablescape/state";

type Props = {
  context?: "productForm" | "productSelector" | "cart";
  optionCategories?: Maybe<string[]>;
  productName?: string;
  selected: CartProductVariant | undefined;
  setSelected: (variant: CartProductVariant["id"]) => void;
  showProductName?: boolean;
  size?: "tiny" | "mini" | "regular";
  variants: CartProductVariant[];
};

const ColorVariantSelectorBase = ({
  context,
  optionCategories,
  productName,
  selected,
  setSelected,
  showProductName,
  size = "regular",
  variants,
}: Props) => {
  const renderOption = useCallback(
    ({
      active,
      checked,
      currentOption,
      currentVariant,
    }: CartRenderVariantOptions) =>
      currentOption && (
        <ColorSwatch
          active={active}
          alt={currentVariant.title}
          available={
            currentVariant.availableForSale &&
            (currentVariant.shippingNote
              ? handleize(currentVariant.shippingNote) !==
                SelectedVariantAvailability.SOLD_OUT
              : true)
          }
          backordered={
            !currentVariant.disableWaitlist &&
            (currentVariant.shippingNote
              ? handleize(currentVariant.shippingNote) !==
                SelectedVariantAvailability.SOLD_OUT
              : false)
          }
          checked={checked}
          color={
            `bg-product-${handleize(currentOption.value)}` as TTailwindString
          }
          optionLabel={currentVariant.optionLabel}
          size={size}
        />
      ),
    [size]
  );

  const colorSelectedOption = useMemo(
    () =>
      selected
        ? findSelectedOption({ type: "color", variant: selected })
        : false,
    [selected]
  );

  return colorSelectedOption ? (
    <VariantSelector
      context={context}
      displaySingleVisibleVariant
      optionCategories={optionCategories}
      productName={productName}
      renderOption={renderOption}
      selected={selected}
      selectedOption={colorSelectedOption}
      setSelected={setSelected}
      showProductName={showProductName}
      type="color"
      variants={variants}
    />
  ) : null;
};

export const ColorVariantSelector = memo(ColorVariantSelectorBase);
